import {
  AlertTriangle,
  BarChart3,
  CalendarDays,
  CheckSquare,
  DollarSign,
  FileText,
  Search,
  Shield,
} from 'lucide-react';

export type DomainWorkflow = {
  id: string;
  title: string;
  subtitle: string;
  capabilityCards: Array<{
    id: string;
    title: string;
    description: string;
    icon: any;
    inputs: string[];
    outputs: string[];
  }>;
};

export const DOMAIN_WORKFLOWS: Record<string, DomainWorkflow> = {
  hr: {
    id: 'hr',
    title: 'Hiring & People',
    subtitle: 'Structured workflows for recruiting and people operations',
    capabilityCards: [
      { id: 'jd', title: 'Job Description Drafting', description: 'Create role-based JD drafts with hiring context.', icon: FileText, inputs: ['Role', 'Level', 'Must-have skills', 'Team context'], outputs: ['JD Draft', 'Must-have matrix', 'Interview focus areas'] },
      { id: 'resume', title: 'Resume Screening', description: 'Score resumes against role criteria.', icon: Search, inputs: ['JD', 'Resumes', 'Scoring rubric'], outputs: ['Ranked shortlist', 'Candidate scorecards', 'Risk flags'] },
      { id: 'schedule', title: 'Interview Scheduling', description: 'Generate scheduling options and panel plans.', icon: CalendarDays, inputs: ['Panel availability', 'Candidate slots', 'Interview stages'], outputs: ['Schedule options', 'Calendar-ready slots', 'Coordination summary'] },
    ],
  },
  finance: {
    id: 'finance',
    title: 'Reports / Ledger',
    subtitle: 'Finance workflows with traceable, audit-ready outputs',
    capabilityCards: [
      { id: 'expense', title: 'Expense Categorization', description: 'Categorize transactions by account and policy.', icon: DollarSign, inputs: ['Transactions', 'Chart of accounts', 'Policy rules'], outputs: ['Categorized ledger', 'Anomaly flags', 'Policy exceptions'] },
      { id: 'invoice', title: 'Invoice Parsing', description: 'Extract structured invoice fields for review.', icon: FileText, inputs: ['Invoice document', 'Vendor data', 'Validation rules'], outputs: ['Parsed invoice JSON', 'Mismatch report', 'Payable summary'] },
      { id: 'reconcile', title: 'Reconciliation Summaries', description: 'Summarize reconciliations across statements.', icon: BarChart3, inputs: ['Bank statements', 'Ledger entries', 'Date range'], outputs: ['Reconciliation summary', 'Unmatched entries', 'Closure checklist'] },
    ],
  },
  legal: {
    id: 'legal',
    title: 'Contracts',
    subtitle: 'Legal review workflows for risk and compliance readiness',
    capabilityCards: [
      { id: 'review', title: 'Contract Review', description: 'Review contracts and identify key obligations.', icon: Shield, inputs: ['Contract text', 'Jurisdiction', 'Deal terms'], outputs: ['Contract summary', 'Obligation table', 'Negotiation notes'] },
      { id: 'clause', title: 'Clause Flagging', description: 'Flag risky or missing clauses.', icon: AlertTriangle, inputs: ['Clause library', 'Contract text', 'Risk policy'], outputs: ['Risky clause list', 'Redline suggestions', 'Risk rating'] },
      { id: 'compliance', title: 'Compliance Checklists', description: 'Generate compliance checklists from contract context.', icon: CheckSquare, inputs: ['Regulatory scope', 'Contract type', 'Policy baseline'], outputs: ['Checklist', 'Gap list', 'Remediation tasks'] },
    ],
  },
};
